import { useContext, useEffect, useState } from "react";
import { DatabaseSearchContext, DateView } from "nystem-components";

const ViewListTibberGraph = () => {
  const { search, loading } = useContext(DatabaseSearchContext);
  const [now, setNow] = useState(Date.now());
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(timer);
  }, []);

  const start = Math.floor(now / 900000) * 900000;
  const prices = (search.data || [])
    .filter(({ _crdate, price }) => _crdate >= start && _crdate < start + 86400000 && Number.isFinite(price))
    .sort((a, b) => a._crdate - b._crdate);
  if (!prices.length)
    return (
      <p className="py-6 text-sm text-gray-400">{loading ? "Loading prices…" : "Prices are not available yet."}</p>
    );

  const minimum = Math.floor(Math.min(...prices.map(({ price }) => price))) - 1;
  const maximum = Math.ceil(Math.max(...prices.map(({ price }) => price))) + 1;
  const cheapest = prices.reduce((lowest, price) => (price.price < lowest.price ? price : lowest));
  const active =
    prices.find(({ _id }) => _id === selected) || prices.find(({ _crdate }) => _crdate === start) || prices[0];

  return (
    <section className="mt-4" aria-label="Tibber electricity prices">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold text-gray-100">Electricity prices</h3>
        <span className="text-xs text-gray-400">15 min · öre/kWh</span>
      </div>
      <div className="mt-3 flex items-baseline justify-between gap-3">
        <div className="flex flex-wrap items-baseline gap-2">
          <strong className="text-2xl font-semibold text-sky-300">{active.price.toFixed(1)}</strong>
          <span className="flex items-center gap-1 text-xs text-gray-400">
            {active._crdate === start && <span>Now ·</span>}
            <DateView model={{ dateFormat: "time", renderAs: "span" }} value={active._crdate} />
            <span>–</span>
            <DateView model={{ dateFormat: "time", renderAs: "span" }} value={Number(active._crdate) + 900000} />
          </span>
        </div>
        <div className="text-right text-xs text-emerald-300">
          <span>Lowest {cheapest.price.toFixed(1)} · </span>
          <DateView model={{ dateFormat: "timeDay", renderAs: "span" }} value={cheapest._crdate} />
        </div>
      </div>
      <div className="relative mt-3 pl-9">
        <div className="pointer-events-none absolute inset-y-0 left-0 flex flex-col justify-between pb-2 text-[10px] text-gray-500">
          <span>{Math.ceil(maximum)}</span>
          <span>{Math.round((maximum + minimum) / 2)}</span>
          <span>{Math.floor(minimum)}</span>
        </div>
        <svg
          className="h-48 w-full overflow-visible"
          viewBox="0 0 960 210"
          preserveAspectRatio="none"
          aria-label="Next 24 hours, one bar per 15 minutes"
        >
          {[20, 110, 200].map((y) => (
            <line
              key={y}
              x1="0"
              x2="960"
              y1={y}
              y2={y}
              stroke="currentColor"
              className="text-gray-700"
              strokeDasharray="3 5"
            />
          ))}
          {prices.map(({ _id, _crdate, price }) => {
            const y = 200 - ((price - minimum) / (maximum - minimum)) * 180;
            return (
              <g
                key={_id}
                tabIndex={0}
                role="button"
                aria-label={`Price ${price.toFixed(1)} öre per kWh`}
                aria-pressed={active._id === _id}
                className={`cursor-pointer outline-none transition-colors ${active._id === _id ? "fill-sky-300" : price === cheapest.price ? "fill-emerald-400" : price > (maximum + minimum) / 2 ? "fill-amber-400/80" : "fill-sky-600"} focus:stroke-white focus:stroke-2`}
                onMouseEnter={() => setSelected(_id)}
                onFocus={() => setSelected(_id)}
                onClick={(event) => {
                  event.preventDefault();
                  setSelected(_id);
                }}
                onKeyDown={(event) => {
                  if (["Enter", " "].includes(event.key)) {
                    event.preventDefault();
                    setSelected(_id);
                  }
                }}
              >
                <rect x={((_crdate - start) / 900000) * 10 + 1} y={y} width="8" height={Math.max(2, 200 - y)} rx="1" />
                <rect
                  x={((_crdate - start) / 900000) * 10}
                  y="0"
                  width="10"
                  height="210"
                  fill="transparent"
                  stroke="none"
                />
              </g>
            );
          })}
        </svg>
        <div className="relative mt-2 h-12 text-[10px] leading-none text-gray-400">
          {Array.from({ length: 25 }, (_, hour) => Math.ceil(start / 3600000) * 3600000 + hour * 3600000)
            .filter((time) => time <= start + 86400000)
            .map((time) => (
              <span
                key={time}
                className="absolute top-0 whitespace-nowrap [writing-mode:vertical-rl]"
                style={{ left: `${((time - start) / 86400000) * 100}%`, transform: "translateX(-50%)" }}
              >
                <DateView model={{ dateFormat: "H", renderAs: "span" }} value={time} />
              </span>
            ))}
        </div>
      </div>
      <p className="mt-2 text-[10px] text-gray-400">Next 24 hours · Select a bar for its price and time.</p>
    </section>
  );
};

export default ViewListTibberGraph;
